import { type EventLocation } from '@goodneighbor/api/schemas/maps';

export type Coordinates = {
  lat: number;
  lng: number;
};

export type CityLocation = {
  displayName: string;
  city?: string;
  region?: string;
  country?: string;
  center: Coordinates;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  placeId?: string;
};

type EventViewportPayload = {
  northeast: Coordinates;
  southwest: Coordinates;
};

type EventGeometryPayload = {
  location: Coordinates;
  locationType?: string;
  viewport?: EventViewportPayload;
};

type PlaceGeometryWithLocationType = google.maps.places.PlaceGeometry & {
  location_type?: string;
};

const cityComponentTypes = new Set<string>([
  'locality',
  'postal_town',
  'sublocality',
  'sublocality_level_1',
  'administrative_area_level_3',
  'administrative_area_level_2'
]);

const addressLevelTypesToReject = new Set<string>([
  'street_address',
  'route',
  'premise',
  'subpremise',
  'intersection',
  'airport'
]);

const getComponentByType = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  preferredTypes: string[]
) =>
  components?.find((component) =>
    component.types.some((type) => preferredTypes.includes(type))
  );

const hasCityType = (types?: string[] | null) =>
  types?.some((type) => cityComponentTypes.has(type)) ?? false;

const hasStreetLevelType = (types?: string[] | null) =>
  types?.some((type) => addressLevelTypesToReject.has(type)) ?? false;

const toBounds = (viewport?: google.maps.LatLngBounds) => {
  if (!viewport) return undefined;

  const northEast = viewport.getNorthEast();
  const southWest = viewport.getSouthWest();

  return {
    north: northEast.lat(),
    east: northEast.lng(),
    south: southWest.lat(),
    west: southWest.lng()
  };
};

const getLocationType = (
  geometry?: google.maps.places.PlaceGeometry
): string | undefined =>
  (geometry as PlaceGeometryWithLocationType | undefined)?.location_type;

const toViewportPayload = (
  viewport?: google.maps.LatLngBounds
): EventViewportPayload | undefined => {
  if (!viewport) {
    return undefined;
  }

  const northEast = viewport.getNorthEast();
  const southWest = viewport.getSouthWest();

  return {
    northeast: { lat: northEast.lat(), lng: northEast.lng() },
    southwest: { lat: southWest.lat(), lng: southWest.lng() }
  };
};

export function toEventLocationPayload(
  place: google.maps.places.PlaceResult
): EventLocation | null {
  const formattedAddress = place.formatted_address || place.name;
  const geometryLocation = place.geometry?.location
    ? {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      }
    : undefined;

  if (!formattedAddress) {
    return null;
  }

  const geometryPayload: EventGeometryPayload | undefined = geometryLocation
    ? {
        location: geometryLocation,
        locationType: getLocationType(place.geometry),
        viewport: toViewportPayload(place.geometry?.viewport ?? undefined)
      }
    : undefined;

  return {
    formattedAddress,
    placeId: place.place_id ?? undefined,
    addressComponents: place.address_components?.map((component) => ({
      longName: component.long_name,
      shortName: component.short_name,
      types: component.types
    })),
    geometry: geometryPayload
  };
}

export const placeToCityLocation = (
  place: google.maps.GeocoderResult | google.maps.places.PlaceResult
): CityLocation | null => {
  if (hasStreetLevelType(place.types)) {
    return null;
  }

  const addressComponents = place.address_components;
  const cityComponent = getComponentByType(
    addressComponents,
    Array.from(cityComponentTypes)
  );
  const regionComponent = getComponentByType(addressComponents, [
    'administrative_area_level_1'
  ]);
  const countryComponent = getComponentByType(addressComponents, ['country']);

  const geometryLocation = place.geometry?.location;
  const bounds = toBounds(place.geometry?.viewport ?? undefined);

  const center = geometryLocation
    ? { lat: geometryLocation.lat(), lng: geometryLocation.lng() }
    : bounds
      ? {
          lat: (bounds.north + bounds.south) / 2,
          lng: (bounds.east + bounds.west) / 2
        }
      : null;

  if (!center) {
    return null;
  }

  const hasCity = cityComponent || hasCityType(place.types);
  if (!hasCity) {
    return null;
  }

  const placeName = 'name' in place ? place.name : undefined;
  const cityName = cityComponent?.long_name || placeName || undefined;
  const region = regionComponent?.short_name || regionComponent?.long_name;
  const country = countryComponent?.short_name || countryComponent?.long_name;

  const displayNameParts = [cityName, region, country].filter(
    Boolean
  ) as string[];
  const displayName = displayNameParts.join(', ') || place.formatted_address;

  if (!displayName) {
    return null;
  }

  return {
    displayName,
    city: cityName,
    region,
    country,
    center,
    bounds,
    placeId: (place as google.maps.places.PlaceResult | undefined)?.place_id
  };
};

export const reverseGeocodeCity = async (
  coordinates: Coordinates
): Promise<CityLocation | null> => {
  if (typeof window === 'undefined' || !google?.maps?.Geocoder) {
    return null;
  }

  const geocoder = new google.maps.Geocoder();

  try {
    const { results } = await geocoder.geocode({
      location: coordinates
    });

    if (!results || results.length === 0) {
      return null;
    }

    const bestResult = results.find((result) => placeToCityLocation(result));
    const resolvedResult: google.maps.GeocoderResult =
      bestResult ?? results[0]!;
    return placeToCityLocation(resolvedResult);
  } catch (_error) {
    return null;
  }
};
